import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { RootState } from 'store';

import AnimatedModal from 'components/AnimatedModal';
import useIsInsideApp from 'hooks/useIsInsideApp';
import useStoreTranslation from 'hooks/useStoreTranslation';
import MENU_ENTRIES, { MenuEntry, MenuKey, MenuRoles, MenuDisplay } from 'utils/menuEntries';
import { goBackToApp } from 'utils/webViewUtils';

import NavigationList from './NavigationList';
import RequesterProfile from './RequesterProfile';
import { Container, Icon, Logout, Subtitle } from './styles';
export interface Props {
  isAdmin: boolean;
  isApprover: boolean;
  isTravelArranger: boolean;
  fullName: string;
  email: string;
  show: boolean;
  onClose: () => void;
  onLogout: () => Promise<any>;
}

const UserMenu = ({ show, fullName, email, isAdmin, isApprover, isTravelArranger, onClose, onLogout }: Props) => {
  const history = useHistory();
  const { t } = useStoreTranslation();
  const isInsideApp = useIsInsideApp();
  const accountResult = useSelector((state: RootState) => state.user.accountResult);

  const companyName = useMemo(() => accountResult?.companyName || '', [accountResult]);
  const companyCountry = useMemo(() => accountResult?.countryName || '', [accountResult]);
  const companyCountryCode = useMemo(() => accountResult?.countryId || '', [accountResult]);
  const hasCompanyBranches = useMemo(() => accountResult?.hasBranchCompanies || false, [accountResult]);
  const branchCompanyLogged = useMemo(() => accountResult?.branchCompanyLogged || false, [accountResult]);

  const handleEntryClick = (entry: MenuEntry) => {
    switch (entry.key) {
      case MenuKey.Dashboard:
      case MenuKey.Booking:
        history.push(entry.mainRoute);
        onClose();
        break;
      default:
        history.push(entry.mainRoute);
        break;
    }
  };

  const handleLogout = () => {
    if (isInsideApp) {
      goBackToApp();
      return;
    }

    onLogout().then(() => history.push('/login'));
  };

  return (
    <AnimatedModal show={show} onClose={onClose}>
      <Container>
        <RequesterProfile
          name={fullName}
          email={email}
          onClose={onClose}
          companyName={companyName}
          countryName={companyCountry}
          branchCompanyLogged={branchCompanyLogged}
          hasCompanyBranches={hasCompanyBranches}
          countryCode={companyCountryCode}
        />
        <NavigationList
          list={MENU_ENTRIES.filter((entry) => entry.display.includes(MenuDisplay.Mobile)).map((entry) => ({
            icon: <Icon small={entry.iconSmall} src={entry.iconSrc} />,
            name: t(entry.name),
            detail: t(entry.detail),
            onClick: () => handleEntryClick(entry),
            show: entry.roles.includes(MenuRoles.Traveler),
          }))}
        />

        {(isAdmin || isApprover) && <Subtitle>{t('userMenu.administrator')}</Subtitle>}

        <NavigationList
          list={MENU_ENTRIES.filter((entry) => entry.display.includes(MenuDisplay.Mobile)).map((entry) => ({
            icon: <Icon small={entry.iconSmall} src={entry.iconSrc} />,
            name: t(entry.name),
            detail: t(entry.detail),
            onClick: () => handleEntryClick(entry),
            show:
              (entry.roles.includes(MenuRoles.Admin) && isAdmin) ||
              (entry.roles.includes(MenuRoles.Approver) && isApprover) ||
              (entry.roles.includes(MenuRoles.TravelArranger) && isTravelArranger),
          }))}
        />
        <Logout onClick={handleLogout}>{t('userMenu.logout')}</Logout>
      </Container>
    </AnimatedModal>
  );
};

export default UserMenu;
